/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { useState, ReactNode } from 'react';
import { useSpring, animated } from '@react-spring/web';
import useSize from '@hzdg/use-size';
import * as Icons from '@tishman/icons';

import { Text, Box, Flex, SxStyleProp } from '..';

import type { KeyboardEvent, MouseEvent } from 'react';

export type FaqItemProps = {
  /**
   * @param {string} question - FAQ question
   */

  question: string;
  children: ReactNode;
  pageNumber?: number;
  a11ySectionIdPrefix?: string;
  questionSx?: SxStyleProp;
};

export const FaqItem = ({
  question,
  children,
  pageNumber,
  a11ySectionIdPrefix,
  questionSx = {},
}: FaqItemProps): React.JSX.Element => {
  const [showAnswer, setShowAnswer] = useState(false);
  const [{ borderBoxSize }, sizeRef] = useSize();

  const animation = useSpring({
    to: {
      height: showAnswer ? borderBoxSize.blockSize : 0,
      opacity: showAnswer ? 1 : 0,
      transform: `translate3d(0,${showAnswer ? 0 : -20}px,0)`,
    },
  });

  return (
    <Box
      aria-labelledby={`${a11ySectionIdPrefix}-faq-tab-${pageNumber}`}
      data-element="FaqItem"
      data-faq-question={question}
    >
      <Box as="h2" sx={{ mt: 4, position: 'relative', zIndex: 1 }}>
        <Flex
          // a11y: things that expand and collapse should be buttons
          aria-controls={question?.replace(/\s/g, '')}
          aria-expanded={showAnswer}
          onClick={(e: MouseEvent) => {
            e.preventDefault();
            setShowAnswer(!showAnswer);
          }}
          onKeyDown={(e: KeyboardEvent) => {
            e.preventDefault();
            if (e.key === 'Enter' || e.key === ' ') {
              setShowAnswer(!showAnswer);
            }
          }}
          role="button"
          sx={{
            justifyContent: 'space-between',
            cursor: 'pointer',
            position: 'relative',
            zIndex: 2,
          }}
          tabIndex={0}
        >
          {question && (
            <Text
              as="span"
              sx={{
                variant: 'text.faqItemTitle',
                maxWidth: 635,
                ...questionSx,
              }}
            >
              {question}
            </Text>
          )}
          <Box
            sx={{
              flex: '1 0 auto',
              textAlign: 'right',
              ml: 3,
              justifyContent: 'flex-end',
            }}
          >
            {showAnswer ? (
              <Icons.CloseAccordian
                aria-hidden="true"
                style={{ height: 4, width: 20 }}
              />
            ) : (
              <Icons.OpenAccordian
                aria-hidden="true"
                style={{ height: 18, width: 18 }}
              />
            )}
          </Box>
        </Flex>
        <animated.div
          // a11y: this is the element that expands and collapses
          id={question?.replace(/\s/g, '')}
          style={animation}
        >
          <Text ref={sizeRef} sx={{ maxWidth: 635, py: 3, opacity: 0.8 }}>
            {children}
          </Text>
        </animated.div>
      </Box>
    </Box>
  );
};
